/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect } from "react";

import { ScreenController } from "../../model/navigation";
import { useUnityDispatch } from "../../viewModel";
import { setActiveScreen } from "../../viewModel/screens";
import { useNavigationEngine } from "./useNavigationEngine";

/**
 * Registers a ScreenController with the NavigationEngine on mount AND
 * activates it as the current screen in Redux so the engine routes D-pad
 * input to it immediately.
 *
 * On unmount, the controller is unregistered and the active screen is cleared.
 *
 * This hook combines `useScreenController` with the `setActiveScreen` Redux
 * dispatch that is required for input routing to work:
 *
 *   useScreenController  →  engine.registerScreen()  (controller is known)
 *   setActiveScreen      →  Redux activeScreen state  (controller receives input)
 *
 * Use this hook instead of `useScreenController` for the top-level screen
 * component that should own D-pad input while it is mounted.
 *
 * Usage:
 *   const controller = useMemo(() => new MyScreenController(dispatch), [dispatch]);
 *   useActiveScreenController(controller);
 *
 * Pass `enabled={false}` to skip registration entirely (e.g. browser mode
 * where D-pad input is not applicable). The hook is always called to satisfy
 * the rules of hooks — it simply becomes a no-op when disabled.
 */
export function useActiveScreenController(
  controller: ScreenController,
  enabled: boolean = true
): void {
  const engine = useNavigationEngine();
  const dispatch = useUnityDispatch();

  // screenName is readonly on ScreenController — stable for the lifetime of
  // the controller instance. No memoization needed.
  const screenName: string = controller.screenName;

  useEffect((): (() => void) => {
    if (!enabled) {
      return (): void => {};
    }

    engine.registerScreen(controller);
    dispatch(setActiveScreen(screenName));

    return (): void => {
      engine.unregisterScreen(screenName);
      dispatch(setActiveScreen(""));
    };
  }, [engine, dispatch, controller, screenName, enabled]);
}
